import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  ButtonGroup,
  makeStyles,
  useMediaQuery,
  withStyles
} from '@material-ui/core';
import { HeaderButton } from 'Components/Shared/ViewPageHeader/PageHeaderStyles';
import { useSearchParams } from 'react-router-dom';
import { Views } from 'Styles';
import { Grid as GridIcon, List as ListIcon } from 'Components/Shared/Icons';
import { Breadcrumb, Category, Customer } from 'Services/Mongo/Types';
import { TextColor } from '@setvi/shared/enums';
import { PriceGroup } from '@setvi/shared/interfaces';
import {
  ISearch,
  SBreadcrumbs,
  SButton,
  SText
} from '@setvi/shared/components';
import CloseIcon from '@material-ui/icons/Close';
import { CustomerDropdown } from 'componentsV2/customer-dropdown';
import { distances } from '@setvi/shared/styles';
import { SearchFormOptions } from '@setvi/shared/components/intelligence/product-match/enums';
import { SearchType } from '@setvi/shared/components/intelligence/search/interfaces';
import { options } from '@setvi/shared/components/intelligence/search';
import { AISearch } from 'pages/products/hooks/use-ai-search-result';
import { PriceGroupDropdown } from 'componentsV2/price-group-dropdown';

/**
 * Below this viewport width the host's tabs row can't hold both the tab strip
 * (~525px) and the catalog toolbar (search at its floor plus the actions), so
 * sharing one line would truncate the tabs and crush the search. Under it the
 * toolbar leaves the slot and drops into the catalog's own header row, next
 * to the categories/filters collapse toggle (DEV-938).
 */
const SINGLE_ROW_TOOLBAR_MIN_WIDTH = 1500;

const useHeaderStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: distances.px.medium,
    padding: `${distances.px.base} ${distances.px.large}`
  },
  search: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // The search pill: 400px with room to spare, narrowing with the viewport
    // so the actions to its right (Ask AI, Price Lister, the customer
    // selector) stay on screen instead of getting clipped (DEV-938). Scoped
    // to the pill itself — the Ask AI popup renders inline below it and must
    // keep its own input sizes.
    '& > div > .MuiOutlinedInput-root': {
      minWidth: 'unset',
      width: 'clamp(240px, 24vw, 400px)'
    }
  }
});

// The actions keep their size: when the row runs out of space the search
// pill narrows and the host's tab strip scrolls instead (DEV-938).
export const Actions = withStyles({
  root: {
    marginLeft: 'auto',
    display: 'flex',
    gridGap: 12,
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0
  }
})(Box);

interface HeaderProps {
  searchQuery: AISearch;
  loading: boolean;
  selectedCategory: Category;
  actions?: JSX.Element;
  /**
   * The side panel's collapse toggle, supplied by SQuadrantLayout. Sits beside
   * the category title so it reads as "this heading's panel" rather than a
   * floating control — and stays put while the panel opens and closes.
   */
  collapseToggle?: ReactNode;
  aiSearchType?: SearchFormOptions;
  aiSearchActive?: boolean;
  controller?: any;
  aiEnabled?: boolean;
  filters?: {
    customer?: {
      show: boolean;
      value: Customer;
      onChange: (customer: Customer) => void;
    };
    priceGroup?: {
      show: boolean;
      value: PriceGroup;
      onChange: (priceGroup: PriceGroup) => void;
    };
  };
  onSelectCategory: (categoryID: Category['_id']) => void;
  handleSearch?: (val: {
    type: SearchType;
    search: AISearch;
    aiType?: SearchFormOptions;
  }) => void;
  refetchProducts: () => void;
  onSearchClear?: () => void;
  setSearchType?: (type: SearchType) => void;
  /**
   * When set, the search and actions render into this element instead of the
   * header row — so a host with its own toolbar (the Quotes Catalog tab) can
   * keep them on one line, where the default "All Categories" title is
   * dropped. On viewports below the single-row width they fall back to the
   * header row, title included, matching the standalone catalog page.
   */
  toolbarSlot?: HTMLElement | null;
}

export const Header = ({
  controller,
  searchQuery,
  loading,
  actions,
  collapseToggle,
  selectedCategory,
  aiSearchType,
  aiSearchActive,
  handleSearch,
  filters = {},
  aiEnabled = true,
  onSelectCategory,
  refetchProducts,
  onSearchClear,
  setSearchType,
  toolbarSlot
}: HeaderProps) => {
  const classes = useHeaderStyles();
  // noSsr: without it the first render always reports "small", flashing the
  // stacked layout (and remounting the search) on every mount on wide screens.
  const singleRowToolbar = useMediaQuery(
    `(min-width:${SINGLE_ROW_TOOLBAR_MIN_WIDTH}px)`,
    { noSsr: true }
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const allCategories = 'All Categories';

  const breadcrumbs = selectedCategory
    ? ([
        { _id: null as any, name: allCategories },
        ...selectedCategory.breadcrumbs
      ] as Breadcrumb[])
    : [];

  const onClose = () => {
    handleSearch({ type: SearchType.Regular, search: { value: '' } });
    setSearchType(SearchType.Regular);

    if (controller) {
      controller?.abort();
    }
  };

  // Title and the panel toggle share a row, so the toggle sits beside the
  // heading in both states — the Ask AI one included, where the panel is
  // collapsed by default and reopening it is exactly what's needed.
  const titleRow = (
    <Box minWidth={292} display="flex" alignItems="center" gridGap={8}>
      <Box
        display="flex"
        alignItems="start"
        flexDirection="column"
        justifyContent="center">
        {aiSearchActive ? (
          <SButton
            variant="outlined"
            style={{ borderRadius: 120 }}
            onClick={onClose}
            startIcon={<CloseIcon style={{ fontSize: 16 }} />}>
            Close Ask AI
          </SButton>
        ) : (
          <>
            <SText
              size="xl"
              title={selectedCategory ? selectedCategory.name : allCategories}
              weight="bold"
            />
            {!!breadcrumbs?.length && (
              <SBreadcrumbs<Breadcrumb>
                maxItems={2}
                parseBreadcrumb={breadcrumb => breadcrumb?.name}
                onBreadcrumbClick={breadcrumb =>
                  onSelectCategory(breadcrumb?._id)
                }
                items={breadcrumbs}
              />
            )}
          </>
        )}
      </Box>
      {collapseToggle}
    </Box>
  );

  const toolbar = (
    <>
      <Box className={classes.search}>
        <ISearch
          aiEnabled={aiEnabled}
          onClear={onSearchClear}
          handleSearch={handleSearch}
          defaultOption={aiSearchActive ? options[1] : options[0]}
          onCloseAi={onClose}
          searchQuery={searchQuery}
          aiSearchActive={aiSearchActive}
          type={aiSearchType}
          loading={loading}
        />
      </Box>

      <Actions>
        {!!actions && actions}

        {filters?.priceGroup?.show && (
          <PriceGroupDropdown
            newDesign
            style={{ dropdownWrapperWidth: 400 }}
            selectedPriceGroup={filters?.priceGroup?.value}
            onChange={filters?.priceGroup?.onChange}
          />
        )}
        {filters?.customer?.show && (
          <CustomerDropdown
            newDesign
            style={{ dropdownWrapperWidth: 400 }}
            selectedCustomer={filters?.customer.value}
            onSelectCustomer={filters?.customer?.onChange}
          />
        )}

        <ButtonGroup>
          <HeaderButton
            onClick={() => {
              refetchProducts();
              searchParams.set(
                'view',
                searchParams.get('view') === Views.GRID_VIEW
                  ? Views.LIST_VIEW
                  : Views.GRID_VIEW
              );
              setSearchParams(searchParams);
            }}>
            {searchParams.get('view') !== Views.GRID_VIEW ? (
              <GridIcon fill={TextColor.SlateGrey} />
            ) : (
              <ListIcon fill={TextColor.SlateGrey} />
            )}
          </HeaderButton>
        </ButtonGroup>
      </Actions>
    </>
  );

  if (toolbarSlot) {
    // In the single-row layout, only a picked category or an open Ask AI
    // still needs a row of its own.
    const showTitleRow = aiSearchActive || !!selectedCategory;

    // On smaller screens the toolbar skips the slot: the tabs keep their row,
    // and the row below holds the full title ("All Categories" / the picked
    // category, with the collapse toggle beside it) plus the search and
    // actions — the same header the standalone catalog page shows, so the two
    // catalogs read identically (QA follow-up on DEV-938).
    if (!singleRowToolbar)
      return (
        <Box className={classes.container}>
          {titleRow}
          {toolbar}
        </Box>
      );

    return (
      <>
        {createPortal(toolbar, toolbarSlot)}
        {(showTitleRow || !!collapseToggle) && (
          <Box className={classes.container}>{titleRow}</Box>
        )}
      </>
    );
  }

  return (
    <Box className={classes.container}>
      {titleRow}
      {toolbar}
    </Box>
  );
};
